import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Loader2, Search } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { toastError, toastSuccess } from "@/lib/toast";
import { helper } from "@/lib/helper";
import { ResponsiveDialogContent } from "@/components/ResponsiveDialogContent";

type MpesaRow = {
  id: number;
  transaction_code?: string;
  transaction_phone?: string;
  transaction_names?: string;
  transaction_amount?: number | string;
};

type LoanOption = {
  id: number | string;
  loan_number?: string | number;
  customer_name?: string;
  phone_number?: string;
  loan_amount?: number | string;
  balance?: number | string;
};

function parseAmount(a: any) {
  if (a == null) return 0;
  if (typeof a === "number") return a;
  const n = Number(String(a).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function useDebounced<T>(value: T, delay = 500) {
  const [v, setV] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return v;
}

export function ReconcileDialog({
  open,
  onOpenChange,
  mpesa,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  mpesa: MpesaRow | null;
  onSuccess?: () => void;
}) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 500);

  const [loans, setLoans] = useState<LoanOption[]>([]);
  const [loanId, setLoanId] = useState("");
  const [loadingLoans, setLoadingLoans] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setSearch(mpesa?.transaction_phone ?? "");
    setLoanId("");
    setLoans([]);
  }, [open, mpesa]);

  useEffect(() => {
    if (!open) return;

    const q = debouncedSearch.trim();

    if (q.length < 3) {
      setLoans([]);
      setLoanId("");
      return;
    }

    let alive = true;

    async function fetchLoans() {
      try {
        setLoadingLoans(true);

        const res = await apiFetch<any>(
          `/mpesa/unresolved-collections/active-loans?search=${encodeURIComponent(q)}`
        );

        if (!alive) return;

        const list = res?.data ?? res?.loans ?? res ?? [];
        setLoans(Array.isArray(list) ? list : []);
      } catch (e: any) {
        if (!alive) return;
        setLoans([]);
        toastError(e?.message ?? "Failed to search active loans");
      } finally {
        if (alive) setLoadingLoans(false);
      }
    }

    fetchLoans();

    return () => {
      alive = false;
    };
  }, [open, debouncedSearch]);

  const selectedLoan = useMemo(() => {
    return loans.find((l) => String(l.id) === String(loanId)) ?? null;
  }, [loans, loanId]);

  const canSubmit = !!mpesa?.id && !!loanId && !saving;

  async function submit() {
    if (!mpesa?.id) {
      toastError("No M-Pesa transaction selected");
      return;
    }

    if (!loanId) {
      toastError("Select loan first");
      return;
    }

    try {
      setSaving(true);

      const res = await apiFetch<any>(
        `/mpesa/unresolved-collections/${mpesa.id}/reconcile`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            loan_id: loanId,
            search,
          }),
        }
      );

      if (res?.ok === false) {
        toastError(res?.message ?? "Failed to reconcile");
        return;
      }

      toastSuccess(res?.message ?? "Transaction reconciled successfully");
      onSuccess?.();
    } catch (e: any) {
      toastError(e?.message ?? "Failed to reconcile");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <ResponsiveDialogContent>
        <div className="border-b border-border bg-muted/30 px-5 py-4">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">
              Reconcile Transaction
            </DialogTitle>

            {mpesa ? (
              <div className="mt-1 text-sm text-muted-foreground">
                {mpesa.transaction_code ?? `#${mpesa.id}`} •{" "}
                {helper.formatMoneyKES(parseAmount(mpesa.transaction_amount))} •{" "}
                {mpesa.transaction_names ?? "MPESA"}
              </div>
            ) : null}
          </DialogHeader>
        </div>

        <div className="px-5 py-4 space-y-5">
          <div>
            <label className="text-sm font-medium">
              Phone Number or ID Number
            </label>

            <div className="relative mt-2">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={saving}
                className="pl-9"
                placeholder="Enter phone number or ID number"
              />
            </div>

            <p className="mt-1 text-xs text-muted-foreground">
              Type at least 3 characters to search active loans.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium">Active Loan</label>

            <Select value={loanId} onValueChange={setLoanId} disabled={saving || loadingLoans}>
              <SelectTrigger className="mt-2">
                <SelectValue
                  placeholder={
                    loadingLoans
                      ? "Searching active loans..."
                      : loans.length
                        ? "Select active loan"
                        : "No active loan selected"
                  }
                />
              </SelectTrigger>

              <SelectContent>
                {loans.map((loan) => (
                  <SelectItem key={loan.id} value={String(loan.id)}>
                    #{loan.loan_number ?? loan.id} - {loan.customer_name ?? loan.phone_number ?? "Customer"} -{" "}
                    {helper.formatMoneyKES(parseAmount(loan.balance ?? loan.loan_amount))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {loadingLoans ? (
              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Searching active loans...
              </div>
            ) : null}

            {!loadingLoans && debouncedSearch.trim().length >= 3 && loans.length === 0 ? (
              <div className="mt-2 text-xs text-destructive">
                No active loan found for this phone or ID number.
              </div>
            ) : null}
          </div>

          {selectedLoan ? (
            <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
              <div className="font-semibold">
                Loan #{selectedLoan.loan_number ?? selectedLoan.id}
              </div>
              <div className="text-muted-foreground">
                Customer: {selectedLoan.customer_name ?? "-"}
              </div>
              <div className="text-muted-foreground">
                Balance: {helper.formatMoneyKES(parseAmount(selectedLoan.balance ?? selectedLoan.loan_amount))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="border-t border-border px-5 py-4">
          <DialogFooter className="gap-2 sm:gap-3">
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)} disabled={saving}>
              Close
            </Button>

            <Button type="button" onClick={submit} disabled={!canSubmit}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Resolve
            </Button>
          </DialogFooter>
        </div>
      </ResponsiveDialogContent>
    </Dialog>
  );
}